import {
  CHANGE_NOTIFICATION_SETTINGS,
  GET_BRANDS,
  GET_CURRENT_SUB,
  GET_PLANS,
  LOGIN_USER,
  SET_ACTIVE_ACCOUNT,
  SET_ALERT_DETAILS,
  SET_BRAND_DETAILS,
  SET_AI_BRAND_INFO,
  SET_EDIT_DRAFT_DATA,
  SET_EDIT_POST_DATA,
  SET_LOGGED_IN,
  SET_REDIRECT_URL,
  SET_SCHEDULE_DATA,
  SET_TIMEZONES,
  SET_USER,
  SET_USER_ACCOUNTS,
  SHOW_NOTIFICATIONS_BADGE,
  SHOW_DMS_BADGE,
  SHOW_COMMENTS_BADGE,
  SET_NEW_NOTIFICATION,
  TOGGLE_EXPIRED_BANNER,
  SET_PAGE_TITLE,
  RESET,
  SET_MEMBER,
  GET_MEMBERS,
  GET_INVITES,
  SET_NEW_POST_STATUS,
  SET_NEW_CONVERSATIONS,
  GET_TOOLTIP,
  GET_DEBIT_CARDS,
  GET_BANKS,
  SET_PLANNER,
} from "./actions/types";

import axios from "axios";
import { initialState } from "./rootReducer";
import { queryParamsHelper, showAlert } from "utils/utils";
import { store } from "./store";

export const axiosInstance = axios.create({
  baseURL: initialState.api_host,
  headers: { "Content-Type": "application/json", Accept: "application/json" },
});

export const miniAppsAxiosInstance = axios.create({
  baseURL: "https://mini-apps.postpaddy.com",
  headers: { "Content-Type": "application/json", Accept: "application/json" },
});

export const aIAxiosInstance = axios.create({
  //initialState.ai_base_url
  baseURL: initialState.ai_base_url,
  headers: {
    "Content-Type": "application/json",
  },
});

export const authService = () => {
  axiosInstance.interceptors.request.use(
    function (config) {
      // Do something before request is sent
      const token = store.getState().user_token;
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      return config;
    },
    function (error) {
      // Do something with request error
      return Promise.reject(error);
    }
  );

  axiosInstance.interceptors.response.use(
    function (response) {
      // Any status code that lie within the range of 2xx cause this function to trigger
      // Do something with response data
      return response;
    },
    function (error) {
      // Any status codes that falls outside the range of 2xx cause this function to trigger
      // Do something with response error
      if (error.response?.status === 401) {
        setLoggedIn(false);
      }

      return Promise.reject(error);
    }
  );
};

export const registerUser = async (data) => {
  let res;
  try {
    res = await axiosInstance.post(`register`, data);

    if (res.data.status === true) {
      store.dispatch({
        type: LOGIN_USER,
        payload: res.data.data,
      });

      const user_profiles_storage = localStorage.getItem("user_profiles");
      const allUsers = user_profiles_storage
        ? { ...JSON.parse(user_profiles_storage) }
        : {};
      const { user, token, refresh_token } = res.data.data;
      allUsers[user.email] = { ...user, token, refresh_token };

      localStorage.setItem("user_profiles", JSON.stringify(allUsers));

      await getTimezones();
      await getUserAccounts();
    }
  } catch (e) {
    throw e;
  }

  return res;
};

export const loginUser = async (data) => {
  let res;

  try {
    res = await axiosInstance.post(`login`, data);

    if (res.data.status === true) {
      const queryUrl = window.location.search;
      const mobileUrl = localStorage.getItem("mobileUrl");
      if (queryUrl.includes("postpaddy-mobile") || mobileUrl) {
        window.location.href = `com.postpaddy.paddy://?token=${res.data.data.token}&refresh_token=${res.data.data.refresh_token}&expires_at=${res.data.data.expires_in}&success=true`;
        localStorage.removeItem("mobileUrl");
        return;
      }

      store.dispatch({
        type: LOGIN_USER,
        payload: res.data.data,
      });

      const user_profiles_storage = localStorage.getItem("user_profiles");
      const allUsers = user_profiles_storage
        ? { ...JSON.parse(user_profiles_storage) }
        : {};
      const { user, token } = res.data.data;
      allUsers[user.email] = { ...user, token };

      localStorage.setItem("user_profiles", JSON.stringify(allUsers));
      localStorage.setItem("showWelcomePrompt", "true");

      await getUserAccounts();
      await getTimezones();
    }

    return res;
  } catch (e) {
    return e.response;
  }
};

export const resendVerificationEmail = async (data) => {
  let res;

  try {
    res = await axiosInstance.post(`email/verification-notification`, data);
  } catch (e) {
    throw Error(e.response.data?.message);
  }

  return res;
};

export const verifyOtp = async (data) => {
  let res;

  try {
    res = await axiosInstance.post(`user/verify`, data);

    if (res.data.status) {
      await getUser();
    }
  } catch (e) {
    throw Error(e.response.data?.message);
  }

  return res;
};

export const socialLogin = async (provider, search) => {
  let res;

  try {
    res = await axiosInstance.get(`auth/${provider}/callback${search}`);

    if (res.data.status === true) {
      store.dispatch({
        type: LOGIN_USER,
        payload: res.data.data,
      });

      const user_profiles_storage = localStorage.getItem("user_profiles");
      const allUsers = user_profiles_storage
        ? { ...JSON.parse(user_profiles_storage) }
        : {};
      const { user, token } = res.data.data;
      if (user) {
        allUsers[user.email] = { ...user, token };
        localStorage.setItem("user_profiles", JSON.stringify(allUsers));
      }

      await getUserAccounts();
      await getTimezones();
    }

    return res;
  } catch (e) {
    return e.response;
  }
};

export const applyReferralCode = async (code) => {
  if (!code) return;

  try {
    const res = await axiosInstance.post(`apply-referral-code`, { code });

    return res;
  } catch (e) {
    throw e;
  }
};

export const createUserAccount = async (data) => {
  let res;

  try {
    res = await axiosInstance.post(`account`, data);
  } catch (e) {
    throw Error(e.response.data?.message);
  }

  return res;
};

export const getUser = async () => {
  try {
    let res = await axiosInstance.get(`user`);

    if (res.data?.status === true) {
      const user = res.data?.data;
      const user_profiles_storage = localStorage.getItem("user_profiles");
      const allUsers = user_profiles_storage
        ? { ...JSON.parse(user_profiles_storage) }
        : {};
      allUsers[user.email] = user;

      localStorage.setItem("user_profiles", JSON.stringify(allUsers));

      store.dispatch({
        type: SET_USER,
        payload: res.data.data,
      });
    }
  } catch (e) {
    console.log(e);
  }
};

export const getUserAccounts = async () => {
  const accountId = localStorage.getItem("account_id");
  const currentAccountList = store.getState().user_accounts;

  try {
    const res = await axiosInstance.get(`account`);

    if (res?.data?.status === true) {
      const accounts = res.data?.data?.accounts;
      const account1 = accounts[0];
      const tempAccount = accounts.find(
        (item) => item.id === parseInt(accountId)
      );

      const accountsDifference = [];

      for (const item of accounts) {
        if (!currentAccountList?.find((acc) => acc.id === item?.id)) {
          accountsDifference.push(item);
        }
      }

      const newAccount = accountsDifference[0];

      store.dispatch({
        type: SET_USER_ACCOUNTS,
        payload: accounts,
      });

      if (newAccount) {
        store.dispatch({
          type: SET_ACTIVE_ACCOUNT,
          payload: newAccount,
        });
      } else if (tempAccount) {
        store.dispatch({
          type: SET_ACTIVE_ACCOUNT,
          payload: tempAccount,
        });
      } else {
        store.dispatch({
          type: SET_ACTIVE_ACCOUNT,
          payload: account1,
        });
      }
    }

    await getCurrentSub();
    await getMember();
    await getMembers();
    await getUser();
    await getBrands();
    await getAiBrandInfo();

    return res;
  } catch (e) {
    console.log(e);
    return e.response;
  }
};

export const deactivateAccount = async (data) => {
  try {
    const res = await axiosInstance.post(`deactivate`, data);
    return res;
  } catch (e) {
    throw e;
  }
};

/**
 * Fetches user account and resets the active account data
 */
export const getAccount = async () => {
  const accountId = store.getState().active_account?.id;

  try {
    const res = await axiosInstance.get(`account/${accountId}`);
    store.dispatch({
      type: SET_ACTIVE_ACCOUNT,
      payload: res.data.data,
    });
  } catch (error) {
    throw error;
  }
};

export const reactivateAccount = async () => {
  try {
    const res = await axiosInstance.post(`activate`);
    return res;
  } catch (e) {
    throw e;
  }
};

export const setOnboardingViewedStatus = async () => {
  try {
    await axiosInstance.get(`viewedTooltips`);
  } catch (e) {
    console.log(e);
    showAlert({
      msg: "An error occurred. Try again later.",
      type: "danger",
    });
  }
};

export const updateProfile = async (data) => {
  try {
    let res = await axiosInstance.post(`EditProfile`, data);

    return res;
  } catch (e) {
    console.log(e);
  }
};

export const changePassword = async (data) => {
  try {
    let res = await axiosInstance.post(`ChangePassword`, data);

    return res;
  } catch (e) {
    return e.response;
  }
};

export const getTimezones = async () => {
  try {
    let res = await axiosInstance.get(`timezone`);

    if (res.data.status === true) {
      store.dispatch({
        type: SET_TIMEZONES,
        payload: res.data.data,
      });
    }
  } catch (e) {
    console.log(e);
  }
};

export const createBrand = async (data) => {
  const accountId = store.getState().active_account?.id;
  let res;

  try {
    res = await axiosInstance.post(`account/${accountId}/brands`, data);

    if (res.data.status === true) {
      store.dispatch({
        type: SET_BRAND_DETAILS,
        payload: res.data.data,
      });
    }

    return res;
  } catch (e) {
    return e.response;
  }
};

export const getAiBrandInfo = async () => {
  const brand = store.getState().brand;
  if (!brand) return;

  try {
    const res = await axiosInstance.get(`brand/${brand?.id}/brand_info`);
    const info = res.data?.data;
    store.dispatch({
      type: SET_AI_BRAND_INFO,
      payload: info,
    });
  } catch (err) {
    throw err;
  }
};

export const updateAiBrandInfo = async (data) => {
  const brand = store.getState().brand;
  try {
    const res = await axiosInstance.post(`brand/${brand?.id}/brand_info`, data);
    await getAiBrandInfo();
    return res;
  } catch (err) {
    throw err;
  }
};

export const saveAiPostIdea = async (data) => {
  const brand = store.getState().brand;
  try {
    const res = await axiosInstance.post(
      `brand/${brand?.id}/post_content`,
      data
    );
    return res;
  } catch (err) {
    throw err;
  }
};

export const getPostIdeas = async () => {
  const brand = store.getState().brand;
  try {
    const res = await axiosInstance.get(`brand/${brand?.id}/post_content`);
    return res;
  } catch (err) {
    throw err;
  }
};

export const getLastPostIdea = async () => {
  const brand = store.getState().brand;
  try {
    const res = await axiosInstance.get(`brand/${brand?.id}/lastPostIdea`);
    return res;
  } catch (err) {
    throw err;
  }
};

export const saveCaption = async (data, idea_id) => {
  const brand = store.getState().brand;
  const url = idea_id
    ? `brand/${brand.id}/post_content/${idea_id}/post_caption`
    : `brand/${brand.id}/post_content/post_caption/create`;

  try {
    const res = await axiosInstance.post(url, data);
    return res;
  } catch (error) {
    throw error;
  }
};

export const getPlanner = async () => {
  const brand = store.getState().brand;

  const url = `brand/${brand.id}/planner`;

  try {
    const res = await axiosInstance.get(url);
    const planner = res.data?.data;

    store.dispatch({
      type: SET_PLANNER,
      payload: planner,
    });
  } catch (error) {
    store.dispatch({
      type: SET_PLANNER,
      payload: null,
    });
  }
};

export const getBrands = async (page = 1) => {
  const accountId = store.getState().active_account?.id;
  const activeBrand = store.getState().brand;
  if (!accountId) return;

  let res;
  try {
    res = await axiosInstance.get(`account/${accountId}/brands?page=${page}`);
    if (res.data.status === true) {
      const { brands } = res.data.data;

      const newActiveBrand = brands.find(
        (brand) => brand?.id === activeBrand?.id
      );

      if (newActiveBrand) {
        getBrand(newActiveBrand.id);
      }

      store.dispatch({
        type: GET_BRANDS,
        payload: brands,
      });

      if (brands.length && !newActiveBrand) {
        store.dispatch({
          type: SET_BRAND_DETAILS,
          payload: brands[0],
        });
        getBrand(brands[0].id);
      }

      if (!brands.length) {
        store.dispatch({
          type: SET_BRAND_DETAILS,
          payload: null,
        });
      }
    }
  } catch (e) {
    return e.response;
  }

  return res;
};

export const getBrand = async (id) => {
  const brandId = parseInt(id);
  const accountId = store.getState().active_account?.id;
  const activeBrand = store.getState().brand;

  if (!brandId) return;

  try {
    let res = await axiosInstance.get(`account/${accountId}/brands/${brandId}`);

    if (res.data.status === true) {
      if (!activeBrand || brandId === activeBrand?.id) {
        store.dispatch({
          type: SET_BRAND_DETAILS,
          payload: res.data.data,
        });
      }

      await getPlanner();
    }

    return res;
  } catch (e) {
    return e.response;
  }
};

export const updateBrand = async (data, brandId) => {
  const accountId = store.getState().active_account?.id;
  let res;

  data.append("_method", "PUT");

  try {
    res = await axiosInstance.post(
      `account/${accountId}/brands/${brandId}`,
      data
    );

    if (res?.data?.status === true) {
      await getBrand(brandId);
    }

    return res;
  } catch (e) {
    return e.response;
  }
};

export const deleteBrand = async (brandId) => {
  const accountId = store.getState().active_account?.id;
  let res;

  try {
    res = await axiosInstance.delete(`account/${accountId}/brands/${brandId}`);
    return res;
  } catch (e) {
    return e.response;
  }
};

export const deleteMultipleBrands = async (data) => {
  const accountId = store.getState().active_account?.id;
  let res;

  try {
    res = await axiosInstance.delete(
      `account/${accountId}/brands/delete-multiple`,
      data
    );
    return res;
  } catch (e) {
    return e.response;
  }
};

export const getSocials = async (brandId) => {
  const accountId = store.getState().active_account?.id;
  const url = `account/${accountId}/brands/${brandId}/socials`;

  if (!brandId) return;

  try {
    let res = await axiosInstance.get(url);

    return res;
  } catch (e) {
    return e.response;
  }
};

export const connectSocial = async (platform, direct = false) => {
  const brandId = store.getState().brand?.id;
  const { pathname } = window.location;

  let url;
  if (pathname.includes("posts")) {
    url = `${platform}/${brandId}/connect?redirect_type=post`;
  } else if (pathname.includes("onboarding")) {
    url = `${platform}/${brandId}/connect?redirect_type=onboarding`;
  } else {
    url = `${platform}/${brandId}/connect?redirect_type=normal`;
  }

  if (direct) {
    url += "&connect_with=instagram";
  }

  try {
    const res = await axiosInstance.get(url);
    return res;
  } catch (error) {
    throw error;
  }
};

export const removeSocialMediaAccount = async (data) => {
  const { platform, brandId, id } = data;
  let res;

  try {
    res = await axiosInstance.delete(`${platform}/${brandId}/delete/${id}`);
    return res;
  } catch (e) {
    return e.response;
  }
};

export const getMultipartSignedURLS = async (data) => {
  try {
    const response = await axiosInstance.post(
      `/upload/getPartSignedUrls`,
      data
    );
    if (response.data && response.status === 200) {
      return response.data.data;
    } else {
      throw new Error(response.data?.message);
    }
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const completeMultipartUpload = async (data) => {
  try {
    const response = await axiosInstance.post(
      `/upload/completeMultipartUpload`,
      data
    );
    if (response.data && response.status === 200) {
      return response.data.data;
    } else {
      throw new Error(response.data?.message);
    }
  } catch (error) {
    console.log(error);
    throw error;
  }
};

/**
 *
 * @param {string} filename
 * @returns
 */
export const generateUploadLink = async (filename) => {
  const url = `temp-upload-url/${filename}`;

  try {
    const res = await axiosInstance.get(url);
    return res;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

/**
 *
 * @param {Object} file
 * @param {string} uploadUrl
 * @param {string} fileType
 * @returns
 */
export const uploadMedia = async (
  file,
  uploadUrl,
  fileType,
  onUploadProgress = null,
  signal = null
) => {
  try {
    let res = await axios.put(uploadUrl, file, {
      signal: signal,
      headers: {
        "Content-Type": fileType,
      },
      onUploadProgress: onUploadProgress,
    });
    return res;
  } catch (e) {
    console.log(e);
    throw e;
  }
};

export const uploadAndCompressMedia = async (
  file,
  shouldCompressImmediately = true,
  onUploadProgress = null,
  signal = null
) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/medias`;
  const formData = new FormData();
  formData.append("media[]", file);
  formData.append("immediate", shouldCompressImmediately ? "1" : "0");

  try {
    const res = await axiosInstance.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      signal: signal,
      onUploadProgress: onUploadProgress,
    });
    return res;
  } catch (error) {
    throw error;
  }
};

export const uploadDMsMedia = async (fileList) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/medias/dm`;
  const uploads = new FormData();

  for (const file of fileList) {
    uploads.append("media[]", file);
  }

  try {
    let res = await axiosInstance.post(url, uploads);
    return res;
  } catch (e) {
    console.log(e);
    throw e;
  }
};

export const deleteBrandImage = async () => {
  const accountId = store.getState().active_account?.id;
  const brandId = store.getState().brand?.id;
  const url = `account/${accountId}/brands/${brandId}/remove-avatar`;

  try {
    let res = await axiosInstance.delete(url);
    return res;
  } catch (e) {
    throw e;
  }
};

export const createBulkPost = async (data) => {
  const accountId = store.getState().active_account?.id;
  const brandId = store.getState().brand?.id;

  try {
    const res = await axiosInstance.post(
      `account/${accountId}/brand/${brandId}/bulk-queue`,
      data
    );
    return res;
  } catch (error) {
    throw error;
  }
};

export const createMultipleDrafts = async (data) => {
  const brandId = store.getState().brand?.id;

  try {
    const res = await axiosInstance.post(
      `brand/${brandId}/drafts/store-multiple`,
      data
    );
    return res;
  } catch (error) {
    throw error;
  }
};

export const duplicatePosts = async (data) => {
  const brandId = store.getState().brand?.id;

  try {
    const res = await axiosInstance.put(
      `brand/${brandId}/duplicate-posts`,
      data
    );
    return res;
  } catch (error) {
    throw error;
  }
};

export const duplicateDrafts = async (data) => {
  const brandId = store.getState().brand?.id;

  try {
    const res = await axiosInstance.put(
      `brand/${brandId}/duplicate-drafts`,
      data
    );
    return res;
  } catch (error) {
    throw error;
  }
};

export const createPost = async (data) => {
  const { accountId, brandId, post, gmt_offset } = data;

  // For when multibrand channel selection is enabled
  if (brandId === undefined) {
    return await createMultibrandPost(data);
  }

  const url = `account/${accountId}/brand/${brandId}/posts`;

  try {
    let res = await axiosInstance.post(url, { post, gmt_offset });

    if (res?.data?.status === true) {
      await getBrand(brandId);
    }
    return res;
  } catch (e) {
    throw e;
  }
};

export const createMultibrandPost = async (data) => {
  const { accountId, brandId, post, gmt_offset } = data;
  const url = `accounts/${accountId}/posts`;

  try {
    let res = await axiosInstance.post(url, { post, gmt_offset });

    // TODO: check relevance of getting updated brand data at this point
    if (res?.data?.status === true && brandId !== undefined) {
      await getBrand(brandId);
    }
    return res;
  } catch (e) {
    throw e;
  }
};

export const createNewPost = async (postData) => {
  const accountId = store.getState().active_account?.id;
  const brandId = store.getState().brand?.id;

  try {
    const res = await axiosInstance.post(
      `account/${accountId}/brand/${brandId}/posts`,
      postData
    );
    return res;
  } catch (e) {
    throw e;
  }
};

export const getBestTimesToPost = async (data) => {
  const brandId = store.getState().brand?.id;

  try {
    const res = await axiosInstance.post(`brand/${brandId}/best-times`, data);
    return res;
  } catch (error) {
    throw error;
  }
};

export const editPost = async (postId, data) => {
  const accountId = store.getState().active_account?.id;
  const brandId = store.getState().brand?.id;
  const post = data[0];
  const url = `account/${accountId}/brand/${brandId}/posts/${postId}`;

  try {
    let res = await axiosInstance.post(url, { ...post, _method: "PUT" });
    return res;
  } catch (e) {
    throw e;
  }
};

export const deletePost = async (postId) => {
  const accountId = store.getState().active_account?.id;
  const brandId = store.getState().brand?.id;
  const url = `account/${accountId}/brand/${brandId}/posts/${postId}`;

  try {
    let res = await axiosInstance.delete(url);

    return res;
  } catch (e) {
    return e.response;
  }
};

export const deleteMultiplePosts = async (postIds) => {
  const brandId = store.getState().brand?.id;
  const url = `/brand/${brandId}/delete-posts`;

  try {
    let res = await axiosInstance.post(url, { post_ids: postIds });

    return res;
  } catch (e) {
    throw e;
  }
};

export const getPosts = async (params) => {
  const accountId = store.getState().active_account?.id;
  const brandId = store.getState().brand?.id;
  if (!brandId) return;
  // const queryParam = queryParamsHelper(query);
  const url = `account/${accountId}/brand/${brandId}/posts`;

  try {
    let res = await axiosInstance.get(url, {
      params,
    });

    return res;
  } catch (e) {
    return e.response;
  }
};

export const getPost = async (postId) => {
  const accountId = store.getState().active_account?.id;
  const brandId = store.getState().brand?.id;
  const url = `account/${accountId}/brand/${brandId}/posts/${postId}`;

  try {
    const res = await axiosInstance.get(url);

    return res;
  } catch (e) {
    return e.response;
  }
};

/**
 *
 * @param {string} platform The social media platform
 * @param {number} page The page number
 * @returns {Promise}
 */
export const getQueuedPosts = async (platform, page) => {
  const accountId = store.getState().active_account?.id;
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/posts`;

  try {
    const res = await axiosInstance.get(url, {
      params: {
        platform,
        page,
        type: 3, // 3 is for queued posts
      },
    });

    return res;
  } catch (err) {
    throw err;
  }
};

/**
 * Fetches all posts for a brand
 * @param {Object} params - Query parameters for filtering and pagination
 * @param {Function} onDownloadProgress - Callback function for tracking download progress
 * @returns {Promise} - Promise resolving to the response from the API
 */
export const getAllPosts = async (params, onDownloadProgress = null) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/posts`;

  try {
    const res = await axiosInstance.get(url, {
      params,
    });

    return res;
  } catch (err) {
    throw err;
  }
};

/**
 *
 * @param {Object} data - An object containing the array of reordered posts
 * @returns {Promise}
 */
export const reorderQueuedPosts = async (data) => {
  const accountId = store.getState().active_account?.id;
  const brandId = store.getState().brand?.id;
  const url = `account/${accountId}/brand/${brandId}/queued-post/reorder`;

  try {
    const res = await axiosInstance.post(url, data);

    return res;
  } catch (err) {
    throw err;
  }
};

export const createDraft = async (data) => {
  const { brandId, post } = data;

  // For when multibrand channel selection is enabled
  if (brandId === undefined) {
    return await createMultibrandDraft(data);
  }

  const url = `brand/${brandId}/drafts`;

  try {
    let res = await axiosInstance.post(url, { draft: post });
    return res;
  } catch (e) {
    throw e;
  }
};

export const createMultibrandDraft = async (data) => {
  const { accountId, post } = data;
  const url = `accounts/${accountId}/drafts`;

  try {
    let res = await axiosInstance.post(url, { draft: post });
    return res;
  } catch (e) {
    throw e;
  }
};

export const fetchAllDrafts = async (params) => {
  const brandId = store.getState().brand?.id;

  const url = `brand/${brandId}/drafts`;

  try {
    let res = await axiosInstance.get(url, {
      params,
    });
    return res.data;
  } catch (e) {
    throw e;
  }
};

export const editDraft = async (draftId, data) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/drafts/${draftId}`;

  try {
    let res = await axiosInstance.post(url, { ...data, _method: "PUT" });
    return res;
  } catch (e) {
    throw e;
  }
};

export const toggleShareable = async (draftId) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/posts/${draftId}/toggle-shareable`;

  try {
    let res = await axiosInstance.get(url);
    return res;
  } catch (e) {
    throw e;
  }
};

/**
 *
 * @param {Number} draftId ID of draft
 * @param {Object} data An object containing the draft data
 * @returns
 */
export const approveDraft = async (draftId, data) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/drafts/${draftId}/approve`;

  try {
    let res = await axiosInstance.post(url, data);
    return res;
  } catch (e) {
    throw e;
  }
};

export const deleteDraft = async (draftId) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/drafts/${draftId}`;

  try {
    let res = await axiosInstance.delete(url);

    return res;
  } catch (e) {
    throw e;
  }
};

export const deleteMultipleDrafts = async (data) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/drafts/delete-multiple`;

  try {
    let res = await axiosInstance.post(url, data);

    return res;
  } catch (e) {
    throw e;
  }
};

export const addMultipleDraftsToPlanner = async (draftIdList) => {
  const brandId = store.getState().brand?.id;
  const plannerId = store.getState().planner?.id;
  const url = `brand/${brandId}/planner/${plannerId}/add-multiple`;

  const data = {
    draft_ids: draftIdList,
  };

  try {
    let res = await axiosInstance.post(url, data);
    return res;
  } catch (e) {
    throw e;
  }
};

export const getPlannerDrafts = async (page = 1, queryParams = "") => {
  const brandId = store.getState().brand?.id;
  const plannerId = store.getState().planner?.id;
  const url = `brand/${brandId}/planner/${plannerId}/drafts?page=${page}${queryParams}`;

  try {
    let res = await axiosInstance.get(url);
    return res.data;
  } catch (e) {
    throw e;
  }
};

export const getPlannerCalendarDrafts = async (queryParams = "") => {
  const brandId = store.getState().brand?.id;
  const plannerId = store.getState().planner?.id;
  const url = `brand/${brandId}/planner/${plannerId}/drafts${queryParams}`;

  try {
    let res = await axiosInstance.get(url);
    return res.data;
  } catch (e) {
    throw e;
  }
};

export const approvePlanner = async () => {
  const brandId = store.getState().brand?.id;
  const plannerId = store.getState().planner?.id;
  const url = `brand/${brandId}/planner/${plannerId}/approve`;

  try {
    let res = await axiosInstance.post(url);
    return res;
  } catch (e) {
    throw e;
  }
};

export const addWorkflowsToPlanner = async (workflowId) => {
  const brandId = store.getState().brand?.id;
  const plannerId = store.getState().planner?.id;
  const url = `brand/${brandId}/planner/${plannerId}/workflow/${workflowId}/attach`;

  try {
    let res = await axiosInstance.get(url);
    return res;
  } catch (e) {
    throw e;
  }
};

export const approvePlannerWorklists = async () => {
  const brandId = store.getState().brand?.id;
  const plannerId = store.getState().planner?.id;
  const url = `brand/${brandId}/planner/${plannerId}/approve-my-worklists`;

  try {
    let res = await axiosInstance.get(url);
    return res;
  } catch (e) {
    throw e;
  }
};

export const getAllClients = async () => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/clients`;

  try {
    let res = await axiosInstance.get(url);
    return res;
  } catch (e) {
    throw e;
  }
};

export const removeClient = async (client_id) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/clients/${client_id}`;

  try {
    let res = await axiosInstance.delete(url);
    return res;
  } catch (e) {
    throw e;
  }
};

export const shareToClients = async (data) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/clients`;

  try {
    let res = await axiosInstance.post(url, data);
    return res;
  } catch (e) {
    throw e;
  }
};

export const getClientSharedDraft = async (clientId, draftId) => {
  const url = `client/${clientId}/draft/${draftId}`;

  try {
    let res = await axiosInstance.get(url);
    return res;
  } catch (e) {
    throw e;
  }
};

export const verifyClientAccess = async (data, client_id) => {
  const url = `client/${client_id}/verify-access`;

  try {
    let res = await axiosInstance.post(url, data);
    return res;
  } catch (e) {
    throw e;
  }
};

export const resendClientInvite = async (data, client_id) => {
  const url = `client/${client_id}/resend-invite`;

  try {
    let res = await axiosInstance.post(url, data);
    return res;
  } catch (e) {
    throw e;
  }
};

export const getClientDraftComments = async (page = 1, client_id, draft_id) => {
  const url = `client/${client_id}/draft/${draft_id}/comments?page=${page}`;

  try {
    let res = await axiosInstance.get(url);
    return res;
  } catch (e) {
    throw e;
  }
};

export const deleteClientComment = async (clientId, draftId, commentId) => {
  try {
    const res = await axiosInstance.post(
      `client/${clientId}/draft/${draftId}/comments/${commentId}`,
      { _method: "DELETE" }
    );
    return res;
  } catch (error) {
    throw error;
  }
};

export const postClientDraftComment = async (client_id, draft_id, data) => {
  const url = `client/${client_id}/draft/${draft_id}/comments`;

  try {
    let res = await axiosInstance.post(url, data);
    return res;
  } catch (e) {
    throw e;
  }
};

export const replyClientDraftComment = async (client_id, draft_id, data) => {
  const url = `client/${client_id}/draft/${draft_id}/comments`;

  try {
    const res = await axiosInstance.post(url, data);
    return res;
  } catch (error) {
    throw error;
  }
};

export const getClientDraftSuggestions = async (
  page = 1,
  client_id,
  draft_id,
  status
) => {
  const url = `client/${client_id}/draft/${draft_id}/suggestions?page=${page}${
    status ? "" : "&status=0"
  }`;

  try {
    let res = await axiosInstance.get(url);
    return res;
  } catch (e) {
    throw e;
  }
};

export const postClientDraftSuggestion = async (client_id, draft_id, data) => {
  const url = `client/${client_id}/draft/${draft_id}/suggestions`;

  try {
    let res = await axiosInstance.post(url, data);
    return res;
  } catch (e) {
    throw e;
  }
};

export const getPlannerDraftsForClient = async (
  client_id,
  planner_id,
  queryParams
) => {
  const url = `client/${client_id}/planner/${planner_id}/drafts`;

  try {
    let res = await axiosInstance.get(url, {
      params: queryParams,
    });
    return res.data;
  } catch (e) {
    throw e;
  }
};

/**
 *
 * @param {string} platform - The social media platform
 * @returns
 */
export const getSingleQueueSettings = async (platform) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/queue/${platform}`;

  try {
    let res = await axiosInstance.get(url);

    return res;
  } catch (e) {
    throw e;
  }
};

/**
 *
 * @returns {Promise} - returns all queue settings
 */
export const getAllQueueSettings = async () => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/queue`;

  try {
    let res = await axiosInstance.get(url);

    return res;
  } catch (e) {
    throw e;
  }
};

/**
 *
 * @param {Object} data - The data containing the updated queue settings
 * @returns
 */
export const updateQueueSettings = async (data) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/queue`;

  try {
    let res = await axiosInstance.post(url, data);

    return res;
  } catch (e) {
    throw e;
  }
};

/**
 *
 * @param {string} action - `One of [pause, resume]`
 * @returns
 */
export const pauseAllQueues = async (action) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/queue/${action}`;

  try {
    let res = await axiosInstance.post(url);

    return res;
  } catch (e) {
    throw e;
  }
};

/**
 *
 * @param {string} action - One of ["pause", "resume"]
 * @param {Object} data - An object containing the platforms array
 * @returns
 */
export const pauseSelectedPlatformQueue = async (action, data) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/selected-queues/${action}`;

  try {
    let res = await axiosInstance.put(url, data);

    return res;
  } catch (e) {
    throw e;
  }
};

export const getAllConversations = async (platforms) => {
  const brandId = store.getState().brand?.id;
  const url = `brands/${brandId}/dms${
    platforms ? `?platforms=${JSON.stringify(platforms)}` : ""
  }`;

  try {
    const res = await axiosInstance.get(url);

    return res;
  } catch (e) {
    return e.response;
  }
};

export const getAllConversationMessages = async (conversationId) => {
  const brandId = store.getState().brand?.id;
  const url = `brands/${brandId}/conversation/${conversationId}/dms`;
  const account = store.getState().active_account;
  const useEngagement = account?.privilege?.use_engagements;

  if (!useEngagement) return;

  try {
    const res = await axiosInstance.get(url);

    return res;
  } catch (e) {
    return e.response;
  }
};

export const sendDirectMessage = async (conversationId, data) => {
  const brandId = store.getState().brand?.id;
  const url = `brands/${brandId}/conversation/${conversationId}/dms`;
  const account = store.getState().active_account;
  const useEngagement = account?.privilege?.use_engagements;

  if (!useEngagement) return;

  try {
    const res = await axiosInstance.post(url, data);

    return res;
  } catch (e) {
    return e.response;
  }
};

export const getAllComments = async (params) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/comments`;
  const account = store.getState().active_account;
  const useEngagement = account?.privilege?.use_engagements;

  if (!useEngagement) return;

  try {
    const res = await axiosInstance.post(url, params);

    return res;
  } catch (e) {
    return e.response;
  }
};

export const getPostComments = async (platform, postId) => {
  const brandId = store.getState().brand?.id;
  const url = `${platform}/brand/${brandId}/${postId}/comments`;
  const account = store.getState().active_account;
  const useEngagement = account?.privilege?.use_engagements;

  if (!useEngagement) return;

  try {
    const res = await axiosInstance.get(url);

    return res;
  } catch (e) {
    return e.response;
  }
};

export const createPostComment = async (platform, postId, comment) => {
  const brandId = store.getState().brand?.id;
  const url = `${platform}/brand/${brandId}/comments`;
  const account = store.getState().active_account;
  const useEngagement = account?.privilege?.use_engagements;

  if (!useEngagement) return;

  try {
    const res = await axiosInstance.post(url, { comment, post_id: postId });

    return res;
  } catch (e) {
    return e.response;
  }
};

export const replyPostComment = async (
  platform,
  commentId,
  message,
  postId
) => {
  const brandId = store.getState().brand?.id;
  const url = `${platform}/brand/${brandId}/comments/${commentId}`;
  const data = { message };
  const account = store.getState().active_account;
  const useEngagement = account?.privilege?.use_engagements;

  if (!useEngagement) return;

  if (postId) {
    data.post_id = postId;
  }

  try {
    const res = await axiosInstance.post(url, data);

    return res;
  } catch (e) {
    return e.response;
  }
};

export const deletePostComment = async (platform, commentId, postId) => {
  const brandId = store.getState().brand?.id;
  const url = postId
    ? `${platform}/brand/${brandId}/comments/${commentId}/${postId}`
    : `${platform}/brand/${brandId}/comments/${commentId}`;
  const account = store.getState().active_account;
  const useEngagement = account?.privilege?.use_engagements;

  if (!useEngagement) return;
  try {
    const res = await axiosInstance.delete(url);

    return res;
  } catch (e) {
    return e.response;
  }
};

export const getCommentReplies = async (platform, commentId, postId) => {
  const brandId = store.getState().brand?.id;

  // Optimistic ui side effect - return empty response for temp comments
  if (commentId?.includes("temp")) {
    return {
      data: {
        status: true,
        message: "No replies for optimistic comment",
        data: []
      }
    };
  }

  let url = `${platform}/brand/${brandId}/comments/${commentId}`;
  if (postId) {
    url += `?post_id=${postId}`;
  }
  const account = store.getState().active_account;
  const useEngagement = account?.privilege?.use_engagements;

  if (!useEngagement) {
    return {
      data: {
        status: false,
        message: "Engagement privilege required",
        data: []
      }
    };
  }

  try {
    const res = await axiosInstance.get(url);

    return res;
  } catch (e) {
    return e.response;
  }
};

export const hideTwitterComment = async (commentId) => {
  const brandId = store.getState().brand?.id;
  let url = `twitter/brand/${brandId}/comments?hide=${1}&comment_id=${commentId}`;

  const account = store.getState().active_account;
  const useEngagement = account?.privilege?.use_engagements;

  if (!useEngagement) return;

  try {
    const res = await axiosInstance.put(url);

    return res;
  } catch (e) {
    return e.response;
  }
};

export const getAllContentAnalytics = async (socials, query) => {
  const brandId = store.getState().brand?.id;

  if (!brandId) return;

  const url = `/brands/${brandId}/socials/contents/analytics${
    query ? `?${query}` : ""
  }`;

  try {
    const res = await axiosInstance.post(url, { socials });

    return res;
  } catch (e) {
    return e.response;
  }
};

export const getAllAccountAnalytics = async (socials, query) => {
  const brandId = store.getState().brand?.id;

  if (!brandId) return;

  const url = `/brands/${brandId}/socials/channel/analytics${
    query ? `?${query}` : ""
  }`;

  try {
    const res = await axiosInstance.post(url, { socials });

    return res;
  } catch (e) {
    return e.response;
  }
};

export const getPlatformAccountAnalytics = async (
  platform,
  query = "start_date=2023-05-20&end_date=2023-06-20"
) => {
  const brandId = store.getState().brand?.id;
  if (!brandId) return;

  const url = `${platform}/brand/${brandId}/analytics${
    query ? `?${query}` : ""
  }`;

  try {
    const res = await axiosInstance.get(url);

    return res;
  } catch (e) {
    return e.response;
  }
};

export const getAudienceAnalytics = async (
  platform,
  query = "start_date=2023-05-20&end_date=2023-06-20"
) => {
  const brandId = store.getState().brand?.id;

  if (!brandId || !platform) return;

  const url = `${platform}/brand/${brandId}/audience/analytics${
    query ? `?${query}` : ""
  }`;

  try {
    const res = await axiosInstance.get(url);

    return res;
  } catch (e) {
    return e.response;
  }
};

export const getBestPerformingPosts = async () => {
  const brandId = store.getState().brand?.id;
  if (!brandId) return;

  try {
    const res = await axiosInstance.get(
      `brands/${brandId}/socials/performing-posts`
    );

    return res;
  } catch (e) {
    return e.response;
  }
};

export const getCalendarEvents = async (query) => {
  const brandId = store.getState().brand?.id;
  if (!brandId) return;

  const url = `brand/${brandId}/calendar${query ? `?${query}` : ""}`;

  try {
    const res = await axiosInstance.get(url);

    return res;
  } catch (e) {
    return e.response;
  }
};

/**
 *
 * @param {string} postType post type ["reel", "post", "story"]
 * @returns
 */
export const getInstagramGridData = async (postType) => {
  const accountId = store.getState().active_account?.id;
  const brandId = store.getState().brand?.id;

  const url = `account/${accountId}/brand/${brandId}/insta-grids/${postType}`;
  try {
    let res = await axiosInstance.get(url);
    return res;
  } catch (e) {
    throw e;
  }
};

export const reorderInstagramGrid = async (data) => {
  const accountId = store.getState().active_account?.id;
  const brandId = store.getState().brand?.id;

  const url = `account/${accountId}/brand/${brandId}/insta-grids/re-order`;
  try {
    let res = await axiosInstance.post(url, data);
    return res;
  } catch (e) {
    throw e;
  }
};

export const updateAiCredit = async () => {
  const accountId = store.getState().active_account?.id;

  try {
    const res = await axiosInstance.post(`account/${accountId}/credit`);

    if (res.data.status === true) {
      getAccount();
    }
  } catch (error) {
    console.log(error);
  }
};

export const buyAiCredit = async (data, cardId) => {
  const accountId = store.getState().active_account?.id;

  const url = `/payment/${accountId}/buy-ai-credit${
    cardId ? `?pay_with_card=${cardId}` : ""
  }`;
  try {
    const res = await axiosInstance.post(url, data);
    return res;
  } catch (error) {
    throw error;
  }
};

export const getPlans = async () => {
  try {
    const res = await axiosInstance.get(`auth/plans`);

    if (res.data.status === true) {
      store.dispatch({
        type: GET_PLANS,
        payload: res.data.data.plans,
      });
    }
  } catch (error) {
    console.log(error);
  }
};

export const getUserCards = async () => {
  try {
    const res = await axiosInstance.get("user-cards");
    store.dispatch({
      type: GET_DEBIT_CARDS,
      payload: res.data?.data,
    });
  } catch (error) {
    store.dispatch({
      type: GET_DEBIT_CARDS,
      payload: [],
    });
  }
};

export const addDebitCard = async () => {
  const accountId = store.getState().active_account?.id;
  try {
    const res = await axiosInstance.get(
      `account/${accountId}/user-cards/add-new`
    );
    return res;
  } catch (error) {
    throw error;
  }
};

export const setDefaultCard = async (cardId) => {
  try {
    const res = await axiosInstance.get(`user-cards/${cardId}/mark-as-default`);
    return res;
  } catch (error) {
    throw error;
  }
};

export const deleteDebitCard = async (cardId) => {
  try {
    const res = await axiosInstance.delete(`user-cards/${cardId}`);
    return res;
  } catch (error) {
    throw error;
  }
};

export const getBillingHistory = async (page) => {
  const accountId = store.getState().active_account?.id;

  try {
    const res = await axiosInstance.get(
      `account/${accountId}/transactions?page=${page}`
    );
    return res;
  } catch (e) {
    return e.response;
  }
};

/**
 *
 * @param {number} planId
 * @param {string} [code]
 * @param {number} [cardId]
 * @returns
 */
export const subscribe = async (planId, code, cardId) => {
  const accountId = store.getState().active_account?.id;
  const url = code
    ? `payment/${accountId}/new-subscribe/${planId}?dis_code=${code}${
        cardId ? `&pay_with_card=${cardId}` : ""
      }`
    : `payment/${accountId}/new-subscribe/${planId}${
        cardId ? `?pay_with_card=${cardId}` : ""
      }`;

  try {
    let res = await axiosInstance.get(url);

    return res;
  } catch (e) {
    return e.response;
  }
};

/**
 * Verify coupon code
 * @param {string} code - coupon code
 * @returns
 */
export const verifyCoupon = async (code) => {
  const accountId = store.getState().active_account?.id;

  try {
    let res = await axiosInstance.post(
      `account/${accountId}/verify-reward-code`,
      { reward_code: code }
    );

    return res;
  } catch (e) {
    return e.response;
  }
};

export const downgradeSubscription = async (data) => {
  const accountId = store.getState().active_account?.id;

  try {
    let res = await axiosInstance.post(
      `account/${accountId}/subscriptions/downgrade`,
      data
    );

    return res;
  } catch (e) {
    return e.response;
  }
};

/**
 *
 * @param {string} action - action to perform ["pause", "resume"]
 * @param {number} subscriptionId - ID of subscription
 * @returns
 */
export const pauseAutoSubscription = async (action, subscriptionId) => {
  const accountId = store.getState().active_account?.id;

  if (!action || !subscriptionId) return;
  console.log(subscriptionId);
  try {
    const res = await axiosInstance.get(
      `account/${accountId}/subscription/${subscriptionId}/${action}-auto`
    );
    return res;
  } catch (error) {
    return error.response;
  }
};

export const getCurrentSub = async () => {
  const accountId = store.getState().active_account?.id;
  if (!accountId) return;

  try {
    let res = await axiosInstance.get(
      `account/${accountId}/subscription/current`
    );
    if (res.data.status === true) {
      store.dispatch({
        type: GET_CURRENT_SUB,
        payload: res.data.data,
      });
    } else if (res.data.status === false) {
      store.dispatch({
        type: GET_CURRENT_SUB,
        payload: null,
      });
    }
  } catch (e) {
    console.log(e);
  }
};

export const getSubscriptions = async (page) => {
  const accountId = store.getState().active_account?.id;
  if (!accountId) return;

  try {
    let res = await axiosInstance.get(
      `account/${accountId}/subscription?status=1&page=${page}`
    );
    return res;
  } catch (e) {
    return e.response;
  }
};

export const addMoreSlots = async (add_type, data, cardId) => {
  const type =
    add_type === "brand"
      ? "add_brand"
      : add_type === "team"
      ? "add_user"
      : null;

  const accountId = store.getState().active_account?.id;
  const url = `payment/${accountId}/${type}${
    cardId ? `?pay_with_card=${cardId}` : ""
  }`;

  if (!type) return;

  try {
    const res = await axiosInstance.post(url, data);
    return res;
  } catch (error) {
    return error.response;
  }
};

export const linkBrandToSlot = async (id) => {
  const accountId = store.getState().active_account?.id;

  try {
    const res = await axiosInstance.get(
      `account/${accountId}/brand/${id}/link-subscription`
    );
    return res;
  } catch (error) {
    return error;
  }
};

export const linkUserToSlot = async (id) => {
  const accountId = store.getState().active_account?.id;

  try {
    const res = await axiosInstance.get(
      `account/${accountId}/members/${id}/link-subscription`
    );
    return res;
  } catch (error) {
    return error;
  }
};

export const getAllNotifications = async () => {
  const accountId = store.getState().active_account?.id;
  if (!accountId) return;

  const url = `account/${accountId}/notifications`;

  try {
    const res = await axiosInstance.get(url);

    return res;
  } catch (e) {
    return e.response;
  }
};

export const getNotificationSettings = async () => {
  const accountId = store.getState().active_account?.id;
  const url = `account/${accountId}/notification-setting`;

  try {
    const res = await axiosInstance.get(url);

    return res;
  } catch (e) {
    return e.response;
  }
};

export const updateNotificationSettings = async (id, data) => {
  const url = `setting/${id}/notification-setting`;

  try {
    const res = await axiosInstance.post(url, { ...data, _method: "PUT" });

    return res;
  } catch (e) {
    return e.response;
  }
};

export const helpCenter = async (data) => {
  let res;

  try {
    res = await axiosInstance.post(`help-center`, data);
  } catch (e) {
    return e.response;
  }

  return res;
};

export const changeCharacterLimitsNotificationStatus = async () => {
  let res;

  try {
    res = await axiosInstance.get(`viewed_allowed_chars`);

    if (res?.data?.status === true) {
      getUser();
    }
  } catch (e) {
    return e.response;
  }

  return res;
};

export const getPostByFamily = async (family_id) => {
  const accountId = store.getState().active_account?.id;
  const brandId = store.getState().brand?.id;

  try {
    let res = await axiosInstance.get(
      `account/${accountId}/brand/${brandId}/post/${family_id}/get`
    );
    return res;
  } catch (e) {
    return e;
  }
};

export const getDraftByFamily = async (family_id) => {
  const brandId = store.getState().brand?.id;

  try {
    let res = await axiosInstance.get(
      `brand/${brandId}/draft/${family_id}/get`
    );
    return res;
  } catch (e) {
    return e;
  }
};

export const updateGroupedPosts = async (data) => {
  const brandId = store.getState().brand?.id;

  try {
    let res = await axiosInstance.put(`/brand/${brandId}/multiple-posts`, data);
    return res;
  } catch (e) {
    return e;
  }
};

export const updateGroupedDrafts = async (data) => {
  const brandId = store.getState().brand?.id;

  try {
    let res = await axiosInstance.put(
      `/brand/${brandId}/multiple-drafts`,
      data
    );
    return res;
  } catch (e) {
    return e;
  }
};

export const setEditPostData = (data) => {
  store.dispatch({
    type: SET_EDIT_POST_DATA,
    payload: data,
  });
};

export const setEditDraftData = (data) => {
  store.dispatch({
    type: SET_EDIT_DRAFT_DATA,
    payload: data,
  });
};

export const setScheduleData = (data) => (dispatch) => {
  dispatch({
    type: SET_SCHEDULE_DATA,
    payload: data,
  });
};

export const setBrandDetails = (data) => (dispatch) => {
  dispatch({
    type: SET_BRAND_DETAILS,
    payload: data,
  });
};

export const changeNotificationSettings = (data) => (dispatch) => {
  dispatch({
    type: CHANGE_NOTIFICATION_SETTINGS,
    payload: data,
  });
};

export const setRedirectUrl = (url) => {
  store.dispatch({
    type: SET_REDIRECT_URL,
    payload: url,
  });
};

export const setAlertDetails = (data) => {
  store.dispatch({
    type: SET_ALERT_DETAILS,
    payload: data,
  });
};

export const setNewPostStatus = (data) => {
  store.dispatch({
    type: SET_NEW_POST_STATUS,
    payload: data,
  });
};

export const setNewNotification = (data) => {
  store.dispatch({
    type: SET_NEW_NOTIFICATION,
    payload: data,
  });
};

export const showNotificationsBadge = (status) => {
  store.dispatch({
    type: SHOW_NOTIFICATIONS_BADGE,
    payload: status,
  });
};

export const setNewConversations = (data) => {
  store.dispatch({
    type: SET_NEW_CONVERSATIONS,
    payload: data,
  });
};

export const showDmsBadge = (status) => {
  store.dispatch({
    type: SHOW_DMS_BADGE,
    payload: status,
  });
};

export const showCommentsBadge = (status) => {
  store.dispatch({
    type: SHOW_COMMENTS_BADGE,
    payload: status,
  });
};

export const setPageTitle = (title) => {
  store.dispatch({
    type: SET_PAGE_TITLE,
    payload: title,
  });
};

export const setLoggedIn = (status) => {
  if (!status) {
    localStorage.removeItem("free_gift_caption");
    // const reportIssuePrompt = localStorage.getItem("reportIssuePrompt");
    // const username = localStorage.getItem("username");
    // const checkbox = localStorage.getItem("checkbox");
    // const accountId = localStorage.getItem("account_id");
    const user_profiles_storage = localStorage.getItem("user_profiles");
    const user_profiles = user_profiles_storage
      ? { ...JSON.parse(user_profiles_storage) }
      : {};
    const user = store.getState().user;
    window.Intercom?.("shutdown");
    window.Intercom?.("boot", {
      app_id: "vu0ufjy6",
    });

    user && delete user_profiles[user.email];

    store.dispatch({ type: RESET });

    // localStorage.clear();

    // localStorage.setItem("reportIssuePrompt", reportIssuePrompt);
    // localStorage.setItem("username", username);
    // localStorage.setItem("checkbox", checkbox);
    // localStorage.setItem("account_id", accountId);
    // localStorage.setItem("user_profiles", JSON.stringify(user_profiles));
  }

  store.dispatch({
    type: SET_LOGGED_IN,
    payload: status,
  });
};

export const setActiveAccount = (data) => {
  store.dispatch({
    type: SET_ACTIVE_ACCOUNT,
    payload: data,
  });
};

export const setUserAccount = (email) => (dispatch) => {
  const user_profiles_storage = localStorage.getItem("user_profiles");
  const user_profiles = user_profiles_storage
    ? { ...JSON.parse(user_profiles_storage) }
    : {};

  if (!email || !user_profiles[email]) return;

  const { token, refresh_token } = user_profiles[email];

  dispatch({
    type: LOGIN_USER,
    payload: {
      data: user_profiles[email],
      token,
      refresh_token,
    },
  });
};

export const setDisplayExpiredBanner = (status) => (dispatch) => {
  dispatch({
    type: TOGGLE_EXPIRED_BANNER,
    payload: status,
  });
};

export const getDraftComments = async (draftId) => {
  if (!draftId) return;
  const brandId = store.getState().brand?.id;
  try {
    const res = await axiosInstance.get(
      `brand/${brandId}/drafts/${draftId}/comments`
    );
    return res;
  } catch (error) {
    throw error;
  }
};

export const getInvites = async () => {
  const activeAccount = store.getState().active_account;

  let res;
  if (!activeAccount) return;

  try {
    res = await axiosInstance.get(`account/${activeAccount.id}/invite`);
    store.dispatch({
      type: GET_INVITES,
      payload: res.data.data,
    });
  } catch (e) {
    store.dispatch({
      type: GET_INVITES,
      payload: [],
    });
  }

  return res;
};

export const getMember = async () => {
  const activeAccount = store.getState().active_account;
  const user = store.getState().user;

  try {
    const res = await axiosInstance.get(
      `account/${activeAccount.id}/team/${user.id}`
    );
    if (res.data.status === true) {
      const data = res.data.data;
      store.dispatch({
        type: SET_MEMBER,
        payload: data,
      });
    }
  } catch (err) {
    store.dispatch({
      type: SET_MEMBER,
      payload: null,
    });
  }
};

export const getMembers = async () => {
  const activeAccount = store.getState().active_account;

  let res;

  try {
    res = await axiosInstance.get(`account/${activeAccount.id}/team`);

    if (res?.data?.status === true) {
      store.dispatch({
        type: GET_MEMBERS,
        payload: res.data.data.users,
      });
    }
  } catch (error) {
    store.dispatch({
      type: GET_MEMBERS,
      payload: [],
    });
  }
  return res;
};

export const deleteMultipleMembers = async (data) => {
  const accountId = store.getState().active_account?.id;
  let res;

  try {
    res = await axiosInstance.delete(
      `account/${accountId}/team/remove-members`,
      data
    );
    return res;
  } catch (e) {
    return e.response;
  }
};

export const requestFeature = async (data) => {
  let res;

  try {
    res = await axiosInstance.post(`/requestFeature`, data);
    return res;
  } catch (e) {
    throw e;
  }
};

export const getRequestedFeatures = async (page, tag) => {
  let res;

  try {
    res = await axiosInstance.get(`/requestFeature?page=${page}&tag=${tag}`);
    return res;
  } catch (e) {
    throw e;
  }
};

export const voteFeature = async (id, type) => {
  let res;

  try {
    res = await axiosInstance.post(`/requestFeature/${id}/${type}`);
    return res;
  } catch (e) {
    throw e;
  }
};

export const getTooltip = async () => {
  try {
    const res = await axiosInstance.get(`viewed_tooltip`);
    if (res?.data?.status === true) {
      store.dispatch({
        type: GET_TOOLTIP,
        payload: res.data.data,
      });
    }
  } catch (error) {
    store.dispatch({
      type: GET_TOOLTIP,
      payload: {},
    });
  }
};

export const updateTooltip = async (data) => {
  const url = `viewed_tooltip`;
  try {
    let res = await axiosInstance.post(url, data);
    if (res?.data?.status === true) {
      store.dispatch({
        type: GET_TOOLTIP,
        payload: data,
      });
    }

    return res;
  } catch (e) {
    throw e;
  }
};

export const trackEvents = async (data) => {
  try {
    let res = await axiosInstance.post(`track`, data);
    return res;
  } catch (e) {
    throw e;
  }
};

export const handleShortenUrl = async (url) => {
  try {
    // api call to url shortner endpoint
    const res = await axios.post("http://3.86.179.39:8080/api/v1/encode", {
      originalUrl: url,
    });
    return res;
  } catch (error) {
    console.log("an error occurred");
  }
};

export const getNaraConversations = async (page = 1) => {
  const brandId = store.getState().brand?.id;
  if (!brandId) return;

  try {
    const res = await axiosInstance.get(
      `nara/brand/${brandId}/conversation?page=${page}`
    );
    return res;
  } catch (error) {
    throw error;
  }
};

export const createNaraConversation = async () => {
  const brandId = store.getState().brand?.id;

  try {
    const res = await axiosInstance.post(`nara/brand/${brandId}/conversation`);
    return res;
  } catch (error) {
    throw error;
  }
};

export const deleteNaraConversation = async (id) => {
  const brandId = store.getState().brand?.id;
  if (!id) return;

  try {
    const res = await axiosInstance.delete(
      `nara/brand/${brandId}/conversation/${id}`
    );
    return res;
  } catch (error) {
    throw error;
  }
};

export const updateNaraConversation = async (id, data) => {
  const brandId = store.getState().brand?.id;
  if (!id) return;

  try {
    const res = await axiosInstance.put(
      `nara/brand/${brandId}/conversation/${id}`,
      data
    );
    return res;
  } catch (error) {
    throw error;
  }
};

export const naraFeedback = async (data) => {
  const accountId = store.getState().active_account.id;

  try {
    const res = await axiosInstance.post(
      `account/${accountId}/nara/feedback`,
      data
    );
    return res;
  } catch (error) {
    throw error;
  }
};

export const saveMessages = async (id, data) => {
  const brandId = store.getState().brand?.id;

  try {
    const res = await axiosInstance.post(
      `nara/brand/${brandId}/conversation/${id}/message`,
      data
    );
    return res;
  } catch (error) {
    throw error;
  }
};

export const updateNaraMessage = async (conversationId, messageId, data) => {
  const brandId = store.getState().brand?.id;

  try {
    const res = await axiosInstance.put(
      `nara/brand/${brandId}/conversation/${conversationId}/message/${messageId}`,
      data
    );
    return res;
  } catch (error) {
    throw error;
  }
};

export const getNaraMessages = async (id) => {
  const brandId = store.getState().brand?.id;

  try {
    const res = await axiosInstance.get(
      `nara/brand/${brandId}/conversation/${id}/message`
    );
    return res;
  } catch (error) {
    throw error;
  }
};

export const getNaraMessage = async (conversationId, messageId) => {
  const brandId = store.getState().brand?.id;

  try {
    const res = await axiosInstance.get(
      `nara/brand/${brandId}/conversation/${conversationId}/message/${messageId}`
    );
    return res;
  } catch (error) {
    throw error;
  }
};

export const getMediaLibrary = async (page = 1, searchQuery, folderId = "") => {
  const accountId = store.getState().active_account?.id;

  try {
    const res = await axiosInstance.get(
      `/account/${accountId}/mediaLibrary?page=${page}${
        searchQuery ? `&search=${searchQuery}` : ""
      }${folderId ? `&folder_id=${folderId}` : ""}`
    );
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const deleteMediaLibrary = async (mediaId) => {
  const accountId = store.getState().active_account?.id;

  try {
    const res = await axiosInstance.delete(
      `/account/${accountId}/mediaLibrary/${mediaId}`
    );
    return res;
  } catch (error) {
    throw error;
  }
};

export const updateMediaLibraryFile = async (mediaId, body) => {
  const accountId = store.getState().active_account?.id;

  try {
    const res = await axiosInstance.put(
      `account/${accountId}/mediaLibrary/${mediaId}`,
      body
    );

    return res.data;
  } catch (error) {
    throw error;
  }
};

export const deleteMultipleMedia = async (body) => {
  const accountId = store.getState().active_account?.id;

  try {
    const res = await axiosInstance.post(
      `account/${accountId}/mediaLibrary/deleteMultiple`,
      body
    );

    return res.data;
  } catch (error) {
    throw error;
  }
};

export const getMediaLibraryFolders = async () => {
  const accountId = store.getState().active_account?.id;

  try {
    const res = await axiosInstance.get(`/account/${accountId}/folder`);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const createMediaLibraryFolder = async ({ ...data }) => {
  const accountId = store.getState().active_account?.id;

  try {
    const res = await axiosInstance.post(`/account/${accountId}/folder`, data);
    return res;
  } catch (error) {
    throw error;
  }
};

export const getFolderById = async ({ id }) => {
  const accountId = store.getState().active_account?.id;

  try {
    const res = await axiosInstance.get(`/account/${accountId}/folder/${id}`);
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const deleteFolder = async (folderId) => {
  const accountId = store.getState().active_account?.id;

  try {
    const res = await axiosInstance.delete(
      `account/${accountId}/folder/${folderId}`
    );

    return res.data;
  } catch (error) {
    throw error;
  }
};

export const updateMediaLibraryFolder = async (folderId, body) => {
  const accountId = store.getState().active_account?.id;

  try {
    const res = await axiosInstance.put(
      `account/${accountId}/folder/${folderId}`,
      body
    );

    return res;
  } catch (error) {
    throw error;
  }
};

export const storeMediaToMediaLibrary = async (data) => {
  const accountId = store.getState().active_account?.id;

  try {
    const res = await axiosInstance.post(
      `account/${accountId}/mediaLibrary`,
      data
    );

    return res.data;
  } catch (error) {
    throw error;
  }
};

export const getCommissions = async () => {
  try {
    const res = await axiosInstance.get("me/commissions");
    return res;
  } catch (error) {
    throw error;
  }
};

export const getPayouts = async () => {
  try {
    const res = await axiosInstance.get("me/payouts");
    return res;
  } catch (error) {
    throw error;
  }
};

export const requestPayout = async (data) => {
  try {
    const res = await axiosInstance.post("me/payouts", data);
    return res;
  } catch (error) {
    throw error;
  }
};

export const getWallet = async () => {
  try {
    const res = await axiosInstance.get("me/wallet");
    return res;
  } catch (error) {
    throw error;
  }
};

export const fetchBankAccountDetail = async (code, acc_number) => {
  if (!code || !acc_number) return;

  try {
    const res = await axiosInstance.get(
      `/paystack/resolveBank?bank_code=${code}&account_number=${acc_number}`
    );
    return res;
  } catch (error) {
    throw error;
  }
};

export const storeBank = async (data) => {
  try {
    const res = await axiosInstance.post("me/banks", data);
    return res;
  } catch (error) {
    throw error;
  }
};

export const getBanks = async () => {
  try {
    const res = await axiosInstance.get("me/banks");
    if (res?.data?.status === true) {
      store.dispatch({
        type: GET_BANKS,
        payload: res.data?.data?.banks,
      });
    }
  } catch (error) {
    store.dispatch({
      type: GET_BANKS,
      payload: [],
    });
  }
};

export const getPostIdea = async (status, perPage) => {
  const brandId = store.getState().brand?.id;
  let url = `brand/${brandId}/postIdea?seen=${status ?? true}`;

  if (perPage) {
    url += `&perPage=${perPage}`;
  }

  try {
    const res = await axiosInstance.get(url);

    return res;
  } catch (e) {
    return e.response;
  }
};

export const createPostIdea = async (data) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/postIdea`;

  try {
    const res = await axiosInstance.post(url, { ideas: data });

    return res;
  } catch (e) {
    throw e;
  }
};

export const createAutomation = async (data) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/automation`;

  try {
    const res = await axiosInstance.post(url, data);

    return res;
  } catch (e) {
    throw e;
  }
};

export const getAutomation = async (params) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/automation`;

  try {
    const res = await axiosInstance.get(url, {
      params: params,
    });

    return res.data;
  } catch (e) {
    throw e.response;
  }
};

export const getSingleAutomation = async (id) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/automation/${id}`;

  try {
    const res = await axiosInstance.get(url);

    return res;
  } catch (e) {
    return e.response;
  }
};

export const updateAutomation = async (id, data) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/automation/${id}`;

  try {
    const res = await axiosInstance.put(url, data);

    if (res.data.status === true) {
      return res;
    } else {
      throw new Error(res.data.message);
    }
  } catch (e) {
    throw e;
  }
};

export const deleteAutomation = async (ids) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/automation/delete-multiple`;

  try {
    const res = await axiosInstance.post(url, { ids });

    return res;
  } catch (e) {
    throw e;
  }
};

export const duplicateAutomation = async (ids) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/automation/duplicate-multiple`;

  try {
    const res = await axiosInstance.post(url, { ids });

    return res;
  } catch (e) {
    throw e;
  }
};

export const getInstagramData = async (nextUrl) => {
  const brandId = store.getState().brand?.id;
  const url = nextUrl || `brand/${brandId}/instagram-posts`;

  try {
    const res = await axiosInstance.post(url);

    return res;
  } catch (e) {
    return e.response;
  }
};

export const fetchFacebookSearch = async () => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/facebook-acct-search`;

  try {
    const res = await axiosInstance.get(url);

    return res;
  } catch (e) {
    return e.response;
  }
};

export const createApprovalWorkflow = async (data) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/approval-workflows`;

  try {
    const res = await axiosInstance.post(url, data);

    return res;
  } catch (e) {
    throw e;
  }
};

export const getApprovalWorkflows = async (params) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/approval-workflows`;

  try {
    const res = await axiosInstance.get(url, {
      params: params,
    });

    return res.data;
  } catch (e) {
    throw e.response;
  }
};

export const getApprovalWorkflow = async (id) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/approval-workflows/${id}`;

  try {
    const res = await axiosInstance.get(url);

    return res.data;
  } catch (e) {
    return e.response;
  }
};

export const updateApprovalWorkflow = async (data) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/approval-workflows/${data.id}`;

  try {
    const res = await axiosInstance.put(url, data.data);

    if (res.data.status === true) {
      return res;
    } else {
      throw new Error(res.data.message);
    }
  } catch (e) {
    throw e;
  }
};

export const deleteApprovalWorkflow = async (id) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/approval-workflows/${id}`;

  try {
    const res = await axiosInstance.delete(url);

    return res;
  } catch (e) {
    throw e;
  }
};

export const deleteApprovalWorkflowStep = async (workflowId, stepId) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/approval-workflows/${workflowId}/step/${stepId}`;

  try {
    const res = await axiosInstance.delete(url);
    return res.data;
  } catch (e) {
    throw e;
  }
};

export const duplicateApprovalWorkflow = async (workflowId) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/approval-workflows/${workflowId}/copy`;

  try {
    const res = await axiosInstance.get(url);

    return res;
  } catch (e) {
    throw e;
  }
};

export const getWorkLists = async () => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/user-worklists`;

  try {
    const res = await axiosInstance.get(url);

    return res;
  } catch (e) {
    return e.response;
  }
};

export const approveDraftWorkflowList = async (workListId) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/user-worklists/${workListId}/approve`;

  try {
    const res = await axiosInstance.put(url);

    return res;
  } catch (e) {
    return e.response;
  }
};

//CRM
export const getLists = async (params = {}) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/contact-lists`;

  try {
    const res = await axiosInstance.get(url, {
      params: params,
    });

    return res.data;
  } catch (e) {
    throw e.response;
  }
};

export const createList = async (data) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/contact-lists`;

  try {
    const res = await axiosInstance.post(url, data);

    return res;
  } catch (e) {
    throw e;
  }
};

export const getList = async (id) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/contact-lists/${id}`;

  try {
    const res = await axiosInstance.get(url);

    return res;
  } catch (e) {
    throw e;
  }
};

export const updateList = async (data) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/contact-lists/${data.id}`;

  try {
    const res = await axiosInstance.put(url, data);

    return res;
  } catch (e) {
    throw e;
  }
};

export const deleteList = async (id) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/contact-lists/${id}`;

  try {
    const res = await axiosInstance.delete(url);

    return res;
  } catch (e) {
    throw e;
  }
};

export const getAllContacts = async (params) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/contacts`;

  try {
    const res = await axiosInstance.get(url, {
      params: params,
    });

    return res.data;
  } catch (e) {
    throw e.response;
  }
};

export const getListContacts = async (id, params) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/contact-lists/${id}/contacts`;

  try {
    const res = await axiosInstance.get(url, {
      params: params,
    });

    return res.data;
  } catch (e) {
    throw e.response;
  }
};

export const createContact = async (data) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/contacts`;

  try {
    const res = await axiosInstance.post(url, data);

    return res;
  } catch (e) {
    throw e;
  }
};

export const deleteContacts = async (listId, ids) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/contact-lists/${listId}/remove-contacts
`;

  try {
    const res = await axiosInstance.delete(url, { data: { ids } });

    return res;
  } catch (e) {
    throw e;
  }
};

export const deleteContactsPermanently = async (ids) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/contacts/delete-multiple`;

  try {
    const res = await axiosInstance.delete(url, { data: { ids } });

    return res;
  } catch (e) {
    throw e;
  }
};

export const getContact = async (id) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/contacts/${id}`;

  try {
    const res = await axiosInstance.get(url);

    return res;
  } catch (e) {
    throw e;
  }
};

export const updateContact = async (data) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/contacts/${data.id}`;

  try {
    const res = await axiosInstance.put(url, data);

    return res;
  } catch (e) {
    throw e;
  }
};

export const getTags = async (params) => {
  const accountId = store.getState().active_account?.id;
  const url = `account/${accountId}/tags`;

  try {
    const res = await axiosInstance.get(url, {
      params: params,
    });

    return res.data;
  } catch (e) {
    throw e.response;
  }
};

export const createTag = async (data) => {
  const accountId = store.getState().active_account?.id;
  const url = `account/${accountId}/tags`;

  try {
    const res = await axiosInstance.post(url, data);

    return res;
  } catch (e) {
    throw e;
  }
};

export const createNote = async (id, data) => {
  const brandId = store.getState().brand?.id;
  const url = `brands/${brandId}/contacts/${id}/notes`;

  try {
    const res = await axiosInstance.post(url, data);

    return res;
  } catch (e) {
    throw e;
  }
};

export const getNotes = async (id, params) => {
  const brandId = store.getState().brand?.id;
  const url = `brands/${brandId}/contacts/${id}/notes`;

  try {
    const res = await axiosInstance.get(url, {
      params: params,
    });

    return res;
  } catch (e) {
    throw e;
  }
};

export const deleteNote = async (contactId, noteId) => {
  const brandId = store.getState().brand?.id;
  const url = `brands/${brandId}/contacts/${contactId}/notes/${noteId}`;

  try {
    const res = await axiosInstance.delete(url);

    return res;
  } catch (e) {
    throw e;
  }
};

export const getNote = async (contactId, noteId) => {
  const brandId = store.getState().brand?.id;
  const url = `brands/${brandId}/contacts/${contactId}/notes/${noteId}`;

  try {
    const res = await axiosInstance.get(url);

    return res;
  } catch (e) {
    throw e;
  }
};

export const updateNote = async (contactId, noteId, data) => {
  const brandId = store.getState().brand?.id;
  const url = `brands/${brandId}/contacts/${contactId}/notes/${noteId}`;

  try {
    const res = await axiosInstance.put(url, data);

    return res;
  } catch (e) {
    throw e;
  }
};

export const getContactActivities = (contactId, params) => {
  const brandId = store.getState().brand?.id;
  const url = `brands/${brandId}/contacts/${contactId}/activities`;

  try {
    const res = axiosInstance.get(url, {
      params: params,
    });

    return res;
  } catch (error) {
    throw error;
  }
};

export const addContactField = async (data) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/contacts/add-custom-fields`;

  try {
    const res = await axiosInstance.post(url, data);

    return res;
  } catch (e) {
    throw e;
  }
};

export const deleteContactField = async (data) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/contacts/delete-custom-fields`;

  try {
    const res = await axiosInstance.delete(url, { data });

    return res;
  } catch (e) {
    throw e;
  }
};

export const getCustomFields = async () => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/contacts/custom-fields`;

  try {
    const res = await axiosInstance.get(url);

    return res;
  } catch (e) {
    throw e;
  }
};

export const getPromo = async () => {
  const url = `promo`;

  try {
    const res = await axiosInstance.get(url);

    return res;
  } catch (e) {
    throw e;
  }
};

export const addViewedPromo = async (id) => {
  const url = `/promo/${id}/viewed`;

  try {
    const res = await axiosInstance.get(url);

    return res;
  } catch (e) {
    throw e;
  }
};

export const getPostAndInsight = async (platform, body) => {
  const brandId = store.getState().brand?.id;
  const url = `brands/${brandId}/contents/${platform}/top-5`;

  try {
    const res = await axiosInstance.post(url, body);

    return res;
  } catch (e) {
    throw e;
  }
};

export const getChannelAnalytics = async (platform, data) => {
  const brandId = store.getState().brand?.id;
  const url = `brands/${brandId}/socials/${platform}/channel-analytics`;

  try {
    const res = await axiosInstance.post(url, data);

    return res;
  } catch (e) {
    throw e;
  }
};

export const getAudienceGrowth = async (platform, query) => {
  const brandId = store.getState().brand?.id;
  const url = `brands/${brandId}/audience/${platform}/audience-growth?${query}`;

  try {
    const res = await axiosInstance.post(url);

    return res;
  } catch (e) {
    throw e;
  }
};

export const getEngagementTrend = async (platform, data) => {
  const brandId = store.getState().brand?.id;
  const url = `brands/${brandId}/audience/${platform}/engagement-trends`;

  try {
    const res = await axiosInstance.post(url, data);

    return res;
  } catch (e) {
    throw e;
  }
};

export const getPostStreak = async (platform, data) => {
  const brandId = store.getState().brand?.id;
  const url = `brands/${brandId}/socials/${platform}/consistency`;

  try {
    const res = await axiosInstance.post(url, data);

    return res;
  } catch (e) {
    throw e;
  }
};

export const fetchAudienceAnalytics = async (platform, query) => {
  const brandId = store.getState().brand?.id;
  const url = `brands/${brandId}/audience/${platform}/analytics?${query}`;

  try {
    const res = await axiosInstance.post(url);

    return res;
  } catch (e) {
    throw e;
  }
};

export const getContentAnalytics = async (platform, data, nextUrl = null) => {
  const brandId = store.getState().brand?.id;
  const url = nextUrl || `brands/${brandId}/contents/${platform}/analytics`;

  try {
    const res = await axiosInstance.post(url, data);

    return res;
  } catch (e) {
    throw e;
  }
};

export const addCompetitor = async (channel, query) => {
  const brandId = store.getState().brand?.id;
  const url = `brands/${brandId}/competitor/${channel}/analysis`;

  try {
    const res = await axiosInstance.get(url, {
      params: query,
    });

    return res;
  } catch (e) {
    throw e;
  }
};

//Pinterest
export const createBoard = async (socialId, data) => {
  const brandId = store.getState().brand?.id;
  const url = `/brand/${brandId}/social/${socialId}/create-pinterest-boards`;

  try {
    const res = await axiosInstance.post(url, data);

    return res;
  } catch (e) {
    throw e;
  }
};

export const getPinterestBoards = async (socialId) => {
  const brandId = store.getState().brand?.id;
  const url = `/brand/${brandId}/social/${socialId}/pinterest-boards`;

  try {
    const res = await axiosInstance.get(url);

    return res;
  } catch (e) {
    throw e;
  }
};

//idea-tags
export const getIdeaTags = async (params) => {
  const accountId = store.getState().active_account?.id;
  const url = `accounts/${accountId}/idea-tags`;

  try {
    const res = await axiosInstance.get(url, {
      params,
    });

    return res.data;
  } catch (e) {
    throw e.response;
  }
};

export const createIdeaTag = async (data) => {
  const accountId = store.getState().active_account?.id;
  const url = `accounts/${accountId}/idea-tags`;

  try {
    const res = await axiosInstance.post(url, data);

    return res;
  } catch (e) {
    throw e;
  }
};

export const createNewPostIdea = async (data) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/create-idea-post`;

  try {
    const res = await axiosInstance.post(url, data);

    return res;
  } catch (e) {
    throw e;
  }
};

export const updateNewPostIdea = async (data) => {
  const brandId = store.getState().brand?.id;
  const url = `brand/${brandId}/post-ideas/${data.id}`;
  try {
    const res = await axiosInstance.put(url, data);

    return res;
  } catch (e) {
    throw e;
  }
};

// Hashtag Management APIs
export const getHashtags = async (params) => {
  const brandId = store.getState().brand?.id;
  if (!brandId) return;

  const url = `brands/${brandId}/hashtags`;

  try {
    const res = await axiosInstance.get(url, {
      params,
    });
    return res;
  } catch (e) {
    throw e;
  }
};

export const getRecentlyUsedHashtags = async (search = "") => {
  const brandId = store.getState().brand?.id;
  if (!brandId) return;

  const url = `brands/${brandId}/recently-used-hashtags${
    search ? `?search=${search}` : ""
  }`;

  try {
    const res = await axiosInstance.get(url);
    return res;
  } catch (e) {
    throw e;
  }
};

export const createHashtag = async (data) => {
  const brandId = store.getState().brand?.id;
  if (!brandId) return;

  const url = `brands/${brandId}/hashtags`;

  try {
    const res = await axiosInstance.post(url, data);
    return res;
  } catch (e) {
    throw e;
  }
};

export const createGroupHashtag = async (data) => {
  const brandId = store.getState().brand?.id;
  if (!brandId) return;

  const url = `brands/${brandId}/hashtags`;

  try {
    const res = await axiosInstance.post(url, data);
    return res;
  } catch (e) {
    throw e;
  }
};

export const deleteHashtag = async (hashtagId) => {
  const brandId = store.getState().brand?.id;
  if (!brandId) return;

  const url = `brands/${brandId}/hashtags/${hashtagId}`;

  try {
    const res = await axiosInstance.delete(url);
    return res;
  } catch (e) {
    throw e;
  }
};
